import { useState } from 'react'
import { IconFilter, IconX, IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader } from '@/components/ui/card'

export interface AdvancedFiltersState {
  preu_min?: string
  preu_max?: string
  dies_arribada?: string
  estat_pagament?: string
  contactat?: boolean
  prioritat?: boolean
  sort_by?: string
  sort_order?: string
}

interface AdvancedFiltersProps {
  filters: AdvancedFiltersState
  onFilterChange: (filters: AdvancedFiltersState) => void
  onClear: () => void
}

export function AdvancedFilters({ filters, onFilterChange, onClear }: AdvancedFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const handleChange = (key: keyof AdvancedFiltersState, value: any) => {
    onFilterChange({
      ...filters,
      [key]: value === '' ? undefined : value,
    })
  }

  const activeFiltersCount = Object.values(filters).filter(v => v !== undefined && v !== '').length

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IconFilter className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-semibold text-lg">Filtres Avançats</h3>
            {activeFiltersCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activeFiltersCount} actius
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFiltersCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClear}
              >
                <IconX className="h-4 w-4 mr-1" />
                Netejar tot
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <>
                  <IconChevronUp className="h-4 w-4 mr-1" />
                  Amagar
                </>
              ) : (
                <>
                  <IconChevronDown className="h-4 w-4 mr-1" />
                  Mostrar
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* Filters Grid */}
      {isExpanded && (
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Precio Mínimo */}
          <div className="space-y-2">
            <Label htmlFor="preu_min">Preu Mínim (€)</Label>
            <Input
              id="preu_min"
              type="number"
              placeholder="0"
              value={filters.preu_min || ''}
              onChange={(e) => handleChange('preu_min', e.target.value)}
              min="0"
              step="10"
            />
          </div>

          {/* Precio Máximo */}
          <div className="space-y-2">
            <Label htmlFor="preu_max">Preu Màxim (€)</Label>
            <Input
              id="preu_max"
              type="number"
              placeholder="1000"
              value={filters.preu_max || ''}
              onChange={(e) => handleChange('preu_max', e.target.value)}
              min="0"
              step="10"
            />
          </div>

          {/* Días de Llegada */}
          <div className="space-y-2">
            <Label htmlFor="dies_arribada">Arriben en (dies)</Label>
            <Select
              value={filters.dies_arribada || 'all'}
              onValueChange={(value) => handleChange('dies_arribada', value === 'all' ? undefined : value)}
            >
              <SelectTrigger id="dies_arribada">
                <SelectValue placeholder="Tots" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tots</SelectItem>
                <SelectItem value="1">Avui</SelectItem>
                <SelectItem value="3">Pròxims 3 dies</SelectItem>
                <SelectItem value="7">Pròxima setmana</SelectItem>
                <SelectItem value="15">Pròxims 15 dies</SelectItem>
                <SelectItem value="30">Pròxim mes</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Estado de Pago */}
          <div className="space-y-2">
            <Label htmlFor="estat_pagament">Estat de Pagament</Label>
            <Select
              value={filters.estat_pagament || 'all'}
              onValueChange={(value) => handleChange('estat_pagament', value === 'all' ? undefined : value)}
            >
              <SelectTrigger id="estat_pagament">
                <SelectValue placeholder="Tots" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tots</SelectItem>
                <SelectItem value="PAGAT">Pagat</SelectItem>
                <SelectItem value="PENDENT">Pendent</SelectItem>
                <SelectItem value="PARCIAL">Parcial</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Contactado */}
          <div className="space-y-2">
            <Label htmlFor="contactat">Contactat</Label>
            <Select
              value={filters.contactat === undefined ? 'all' : filters.contactat.toString()}
              onValueChange={(value) => 
                handleChange('contactat', value === 'all' ? undefined : value === 'true')
              }
            >
              <SelectTrigger id="contactat">
                <SelectValue placeholder="Tots" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tots</SelectItem>
                <SelectItem value="true">Sí</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Prioridad */}
          <div className="space-y-2">
            <Label htmlFor="prioritat">Prioritat</Label>
            <Select
              value={filters.prioritat === undefined ? 'all' : filters.prioritat.toString()}
              onValueChange={(value) => 
                handleChange('prioritat', value === 'all' ? undefined : value === 'true')
              }
            >
              <SelectTrigger id="prioritat">
                <SelectValue placeholder="Tots" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tots</SelectItem>
                <SelectItem value="true">Sí</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Ordenar por */}
          <div className="space-y-2">
            <Label htmlFor="sort_by">Ordenar per</Label>
            <Select
              value={filters.sort_by || 'default'}
              onValueChange={(value) => handleChange('sort_by', value === 'default' ? undefined : value)}
            >
              <SelectTrigger id="sort_by">
                <SelectValue placeholder="Data entrada" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Per defecte</SelectItem>
                <SelectItem value="preu">Preu</SelectItem>
                <SelectItem value="data_entrada">Data entrada</SelectItem>
                <SelectItem value="data_creacio">Data creació</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Orden */}
          <div className="space-y-2">
            <Label htmlFor="sort_order">Ordre</Label>
            <Select
              value={filters.sort_order || 'desc'}
              onValueChange={(value) => handleChange('sort_order', value)}
            >
              <SelectTrigger id="sort_order">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">Ascendent</SelectItem>
                <SelectItem value="desc">Descendent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          </div>
        </CardContent>
      )}

      {/* Quick Filters (Always Visible) */}
      {!isExpanded && activeFiltersCount > 0 && (
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-2">
          {filters.preu_min && (
            <Badge variant="secondary">
              Preu min: {filters.preu_min}€
              <button
                onClick={() => handleChange('preu_min', undefined)}
                className="ml-1 hover:text-red-600"
              >
                <IconX className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.preu_max && (
            <Badge variant="secondary">
              Preu max: {filters.preu_max}€
              <button
                onClick={() => handleChange('preu_max', undefined)}
                className="ml-1 hover:text-red-600"
              >
                <IconX className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.dies_arribada && (
            <Badge variant="secondary">
              Arriben en {filters.dies_arribada} dies
              <button
                onClick={() => handleChange('dies_arribada', undefined)}
                className="ml-1 hover:text-red-600"
              >
                <IconX className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.estat_pagament && (
            <Badge variant="secondary">
              Pagament: {filters.estat_pagament}
              <button
                onClick={() => handleChange('estat_pagament', undefined)}
                className="ml-1 hover:text-red-600"
              >
                <IconX className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.contactat !== undefined && (
            <Badge variant="secondary">
              {filters.contactat ? 'Contactats' : 'Sense contactar'}
              <button
                onClick={() => handleChange('contactat', undefined)}
                className="ml-1 hover:text-red-600"
              >
                <IconX className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.prioritat !== undefined && (
            <Badge variant="secondary">
              {filters.prioritat ? 'Prioritàries' : 'No prioritàries'}
              <button
                onClick={() => handleChange('prioritat', undefined)}
                className="ml-1 hover:text-red-600"
              >
                <IconX className="h-3 w-3" />
              </button>
            </Badge>
          )}
          </div>
        </CardContent>
      )}
    </Card>
  )
}
